import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin, Mail, Linkedin, ChevronLeft, ChevronRight, Globe } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import { STATS, WHY_US, CONTACT, LEADERSHIP } from "@/data/site";
import SEO from "@/components/SEO";

const TIMELINE = [
  { year: "2020", title: "AmagicTech founded", body: "Started as a two-person studio in Coimbatore with one client and a clear thesis: engineering rigor for ambitious teams." },
  { year: "2021", title: "First international launch", body: "Shipped our first cross-border project for a client in Oman — the start of our Middle East footprint." },
  { year: "2023", title: "Muscat office opens", body: "Opened our second office in Muscat to serve clients across the GCC closer to home." },
  { year: "2024", title: "AI & automation practice", body: "Launched a dedicated AI practice — production copilots, document understanding, and forecasting." },
  { year: "2026", title: "50+ clients, 28+ launches", body: "Today we serve businesses across India, the Middle East, Europe, and North America." },
];

/* ─── region tag colour ─────────────────────────────────────────────────────── */
const REGION_STYLE = {
  India: "bg-orange-50 text-orange-700 border-orange-200",
  GCC:   "bg-sky-50   text-sky-700   border-sky-200",
};

/* ════════════════════════════════════════════════════════════════════════════
   LEADER CARD — desktop interactive spotlight card
   ════════════════════════════════════════════════════════════════════════════ */
function LeaderCard({ leader, idx, isActive, onClick }) {
  const regionCls = REGION_STYLE[leader.region] ?? "bg-zinc-100 text-zinc-600 border-zinc-200";

  return (
    <motion.article
      data-testid={`leader-card-${idx}`}
      layout
      onClick={onClick}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: idx * 0.08, ease: [0.22, 0.61, 0.36, 1] }}
      className={`group relative overflow-hidden rounded-2xl cursor-pointer border transition-all duration-500 select-none
        ${isActive
          ? "border-zinc-900 shadow-2xl shadow-zinc-900/10"
          : "border-zinc-200 hover:border-zinc-400 hover:shadow-xl hover:shadow-zinc-100/60"
        }`}
    >
      {/* ── photo ── */}
      <div className={`relative overflow-hidden bg-zinc-100 transition-all duration-500 ${isActive ? "aspect-[3/2]" : "aspect-[4/5]"}`}>
        <img
          src={leader.image}
          alt={leader.name}
          loading="lazy"
          className={`w-full h-full object-cover transition-all duration-700
            ${isActive ? "scale-105 grayscale-0" : "grayscale group-hover:grayscale-0 group-hover:scale-105"}`}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />

        {/* gradient overlay */}
        <div className={`absolute inset-0 bg-gradient-to-t transition-opacity duration-500
          ${isActive
            ? "from-zinc-950/70 via-zinc-950/20 to-transparent opacity-100"
            : "from-zinc-900/40 via-transparent to-transparent opacity-0 group-hover:opacity-100"
          }`}
        />

        {/* region badge */}
        <span className={`absolute top-4 left-4 text-[10px] font-mono-tech tracking-[0.18em] uppercase border px-2.5 py-0.5 rounded-full backdrop-blur-md ${regionCls}`}>
          {leader.region}
        </span>

        {/* expand hint when not active */}
        {!isActive && (
          <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
            <span className="inline-flex items-center gap-1.5 text-white text-xs font-mono-tech tracking-wider bg-zinc-900/80 backdrop-blur-md px-3 py-1.5 rounded-full">
              View profile <ArrowUpRight className="w-3 h-3" />
            </span>
          </div>
        )}

        {/* name overlay when active */}
        {isActive && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
            className="absolute bottom-5 left-6 right-6"
          >
            <p className="font-mono-tech text-[10px] tracking-[0.22em] text-zinc-300 uppercase">{leader.role}</p>
            <h3 className="mt-1 font-display text-3xl text-white tracking-tight leading-tight">{leader.name}</h3>
          </motion.div>
        )}
      </div>

      {/* ── body (non-active) ── */}
      {!isActive && (
        <div className="p-5 bg-white">
          <p className="label-mono text-[10px]">{leader.role}</p>
          <h3 className="mt-1.5 font-display text-xl text-zinc-900 tracking-tight">{leader.name}</h3>
          <div className="mt-3 flex items-center gap-2">
            <a
              href={`mailto:${leader.email}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-zinc-200 text-zinc-500 hover:text-zinc-900 hover:border-zinc-500 transition-colors"
              title={`Email ${leader.name}`}
            >
              <Mail className="w-3.5 h-3.5" />
            </a>
            <a
              href={leader.linkedin}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-zinc-200 text-zinc-500 hover:text-zinc-900 hover:border-zinc-500 transition-colors"
              title={`${leader.name} on LinkedIn`}
            >
              <Linkedin className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      )}

      {/* ── expanded panel (active) ── */}
      <AnimatePresence>
        {isActive && (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
            className="overflow-hidden bg-white"
          >
            <div className="p-6 md:p-8">
              <p className="text-zinc-700 text-[15px] leading-relaxed">{leader.bio}</p>
              <p className="mt-3 text-zinc-500 text-sm leading-relaxed">{leader.long}</p>

              <div className="mt-6 pt-5 border-t border-zinc-100 flex flex-wrap items-center gap-3">
                <a
                  href={`mailto:${leader.email}`}
                  data-testid={`leader-email-${idx}`}
                  className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2 text-xs font-medium text-zinc-700 rounded-lg hover:border-zinc-600 hover:text-zinc-900 hover:bg-zinc-50 transition-all"
                >
                  <Mail className="w-3.5 h-3.5" /> {leader.email}
                </a>
                <a
                  href={leader.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`leader-linkedin-${idx}`}
                  className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2 text-xs font-medium text-zinc-700 rounded-lg hover:border-zinc-600 hover:text-zinc-900 hover:bg-zinc-50 transition-all"
                >
                  <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   MOBILE CAROUSEL
   ════════════════════════════════════════════════════════════════════════════ */
function MobileLeaderCarousel() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [expanded, setExpanded] = useState(null);

  const scrollTo = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[i];
    if (card) { track.scrollTo({ left: card.offsetLeft - 20, behavior: "smooth" }); }
    setActive(i);
    setExpanded(null);
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const center = track.scrollLeft + track.clientWidth / 2;
    let closest = 0;
    Array.from(track.children).forEach((child, i) => {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const closestCenter = track.children[closest].offsetLeft + track.children[closest].offsetWidth / 2;
      if (Math.abs(childCenter - center) < Math.abs(closestCenter - center)) closest = i;
    });
    setActive(closest);
  };

  return (
    <div className="md:hidden">
      {/* track */}
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4"
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
      >
        {LEADERSHIP.map((l, i) => {
          const regionCls = REGION_STYLE[l.region] ?? "bg-zinc-100 text-zinc-600 border-zinc-200";
          return (
            <div
              key={l.email}
              className={`snap-center shrink-0 w-[78vw] max-w-[300px] rounded-2xl overflow-hidden border transition-all duration-300 cursor-pointer
                ${i === active ? "border-zinc-900 shadow-xl" : "border-zinc-200"}`}
              onClick={() => setExpanded(expanded === i ? null : i)}
            >
              <div className="relative aspect-[4/5] bg-zinc-100">
                <img
                  src={l.image}
                  alt={l.name}
                  loading="lazy"
                  className={`w-full h-full object-cover transition-all duration-500 ${i === active ? "grayscale-0" : "grayscale"}`}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/60 via-transparent to-transparent pointer-events-none" />
                <span className={`absolute top-3 left-3 text-[9px] font-mono-tech tracking-[0.18em] uppercase border px-2 py-0.5 rounded-full backdrop-blur-md ${regionCls}`}>
                  {l.region}
                </span>
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="font-mono-tech text-[9px] tracking-[0.2em] text-zinc-300 uppercase">{l.role}</p>
                  <h3 className="mt-0.5 font-display text-xl text-white tracking-tight">{l.name}</h3>
                </div>
              </div>

              {/* expandable bio */}
              <AnimatePresence>
                {expanded === i && (
                  <motion.div
                    key="bio"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.35 }}
                    className="overflow-hidden bg-white"
                  >
                    <div className="p-4">
                      <p className="text-zinc-600 text-sm leading-relaxed">{l.bio}</p>
                      <div className="mt-4 flex items-center gap-2">
                        <a href={`mailto:${l.email}`} className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-zinc-200 text-zinc-500 hover:border-zinc-500 transition-colors">
                          <Mail className="w-3.5 h-3.5" />
                        </a>
                        <a href={l.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-zinc-200 text-zinc-500 hover:border-zinc-500 transition-colors">
                          <Linkedin className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* controls */}
      <div className="mt-4 flex items-center justify-between">
        <p className="font-mono-tech text-xs text-zinc-400 tracking-widest">
          {String(active + 1).padStart(2, "0")} / {String(LEADERSHIP.length).padStart(2, "0")}
        </p>
        <div className="flex items-center gap-1.5">
          {LEADERSHIP.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              aria-label={`Leader ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? "w-6 bg-zinc-900" : "w-1.5 bg-zinc-300"}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scrollTo(Math.max(0, active - 1))}
            disabled={active === 0}
            className="w-8 h-8 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-500 disabled:opacity-30 hover:border-zinc-400 hover:text-zinc-900 transition-all"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => scrollTo(Math.min(LEADERSHIP.length - 1, active + 1))}
            disabled={active === LEADERSHIP.length - 1}
            className="w-8 h-8 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-500 disabled:opacity-30 hover:border-zinc-400 hover:text-zinc-900 transition-all"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <p className="mt-3 text-center font-mono-tech text-[10px] text-zinc-400 tracking-widest uppercase">
        Tap a card to read bio
      </p>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   DESKTOP INTERACTIVE GRID
   ════════════════════════════════════════════════════════════════════════════ */
function DesktopLeaderGrid() {
  const [activeIdx, setActiveIdx] = useState(null);
  const toggle = (i) => setActiveIdx(prev => prev === i ? null : i);

  return (
    <div className="hidden md:grid grid-cols-4 gap-4 mt-14">
      {LEADERSHIP.map((leader, idx) => (
        <LeaderCard
          key={leader.email}
          leader={leader}
          idx={idx}
          isActive={activeIdx === idx}
          onClick={() => toggle(idx)}
        />
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   PAGE
   ════════════════════════════════════════════════════════════════════════════ */
export default function About() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About AmagicTech Solutions",
    "url": "https://amagictechsolutions.com/about",
    "description": "Learn about AmagicTech Solutions, our culture, and the leadership team driving digital innovation across India, GCC, and globally.",
    "mainEntity": {
      "@type": "Organization",
      "name": "AmagicTech Solutions",
      "url": "https://amagictechsolutions.com",
      "founder": LEADERSHIP.map((leader) => ({
        "@type": "Person",
        "name": leader.name,
        "jobTitle": leader.role,
        "image": `https://amagictechsolutions.com${leader.image}`,
        "sameAs": leader.linkedin
      }))
    }
  };

  return (
    <main data-testid="about-page" className="pt-32 md:pt-40">
      <SEO
        title="About Us"
        description="Learn about AmagicTech Solutions, our engineering studio, and the leadership team driving innovation in web, mobile, and AI across India & GCC."
        url="/about"
        keywords="about AmagicTech, leadership, engineering studio, software agency Coimbatore, software company Muscat, digital product studio"
        schema={aboutSchema}
      />

      {/* HERO TEXT */}
      <section className="container-tight">
        <Reveal>
          <SectionLabel testid="about-label">About</SectionLabel>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-zinc-900 leading-[0.98]">
            An engineering studio
            <br /><span className="text-zinc-500">built on trust.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1} className="mt-10 grid md:grid-cols-12 gap-10">
          <p className="md:col-span-7 text-zinc-700 text-lg leading-relaxed">
            AmagicTech Solutions is a multidisciplinary team blending user experience design,
            clean engineering practices, and cloud-native operations. We help businesses launch
            products faster and scale with confidence — from composable storefronts to AI copilots
            and consumer-grade mobile apps.
          </p>
          <p className="md:col-span-5 text-zinc-600 text-[15px] leading-relaxed">
            Our distributed team has delivered projects across India, the Middle East, Europe,
            and North America — earning trust through reliable execution and genuine partnership.
            Every engagement includes stakeholder workshops, iterative design reviews, automated
            testing pipelines, and dedicated support long after launch.
          </p>
        </Reveal>
      </section>

      {/* OFFICES */}
      <section className="container-tight mt-24 grid md:grid-cols-2 gap-6">
        {CONTACT.offices.map((o, idx) => (
          <Reveal key={o.city} delay={idx * 0.06}>
            <div className="border border-zinc-200 p-8 md:p-10 h-full bg-[#FAFAF7] hover:border-zinc-400 transition-colors">
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-zinc-600" />
                <p className="label-mono">Office · 0{idx + 1}</p>
              </div>
              <h3 className="mt-5 font-display text-3xl text-zinc-900 tracking-tight">{o.city}</h3>
              <p className="mt-4 text-zinc-600 whitespace-pre-line text-[15px] leading-relaxed">{o.address}</p>
              <p className="mt-4 font-mono-tech text-sm text-zinc-700">{o.phone}</p>
            </div>
          </Reveal>
        ))}
      </section>

      {/* STATS */}
      <section className="mt-24 border-y border-zinc-200">
        <div className="container-tight grid grid-cols-2 md:grid-cols-4 divide-x divide-zinc-200">
          {STATS.map((s) => (
            <div key={s.label} className="py-12 px-4 text-center md:text-left md:px-8">
              <p className="font-mono-tech text-4xl md:text-5xl text-zinc-900 tracking-tight">{s.value}</p>
              <p className="label-mono mt-2">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TIMELINE */}
      <section className="container-tight py-24">
        <Reveal>
          <SectionLabel index="/01" testid="about-timeline-label">Our journey</SectionLabel>
          <h2 className="mt-6 font-display text-4xl md:text-5xl text-zinc-900 tracking-tighter">
            Six years.
            <br /><span className="text-zinc-500">Four continents.</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-3 hidden md:block">
            <p className="label-mono">Timeline</p>
          </div>
          <div className="md:col-span-9 border-l border-zinc-200">
            {TIMELINE.map((t, idx) => (
              <Reveal key={t.year} delay={idx * 0.05}>
                <div className="pl-8 md:pl-12 py-8 border-b border-zinc-200 last:border-b-0 relative">
                  <span className="absolute -left-[5px] top-10 w-2.5 h-2.5 bg-zinc-900" />
                  <p className="font-mono-tech text-xs text-zinc-500">{t.year}</p>
                  <h3 className="mt-2 font-display text-2xl text-zinc-900 tracking-tight">{t.title}</h3>
                  <p className="mt-3 text-zinc-600 text-[15px] leading-relaxed max-w-2xl">{t.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          LEADERSHIP — premium animated section
          ══════════════════════════════════════════════════ */}
      <section className="container-tight pb-28">
        <Reveal>
          <SectionLabel index="/02" testid="about-leadership-label">Leadership</SectionLabel>
          <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-display text-4xl md:text-5xl text-zinc-900 tracking-tighter">
              Clarity of vision.
              <br /><span className="text-zinc-400">Excellence in execution.</span>
            </h2>
            <p className="max-w-sm text-zinc-500 text-[15px] leading-relaxed md:text-right">
              Our team blends regional insight with global standards to deliver scalable,
              reliable, and growth-focused solutions across India and the GCC.
            </p>
          </div>
        </Reveal>

        {/* desktop hint */}
        <Reveal delay={0.1}>
          <p className="hidden md:flex items-center gap-2 mt-5 font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">
            <Globe className="w-3 h-3" /> Click a card to read full profile
          </p>
        </Reveal>

        {/* mobile carousel */}
        <div className="mt-10">
          <MobileLeaderCarousel />
        </div>

        {/* desktop interactive grid */}
        <DesktopLeaderGrid />
      </section>

      {/* VALUES */}
      <section className="container-tight pb-24">
        <Reveal>
          <SectionLabel index="/03" testid="about-values-label">What we value</SectionLabel>
          <h2 className="mt-6 font-display text-4xl md:text-5xl text-zinc-900 tracking-tighter">
            Principles, not posters.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-100 border border-zinc-200">
          {WHY_US.map((w, idx) => (
            <Reveal key={w.title} delay={idx * 0.05}>
              <div className="bg-[#FAFAF7] p-7 h-full">
                <span className="font-mono-tech text-xs text-zinc-500">0{idx + 1}</span>
                <h3 className="mt-6 font-display text-xl text-zinc-900 tracking-tight">{w.title}</h3>
                <p className="mt-2 text-zinc-600 text-[15px] leading-relaxed">{w.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-16 border border-zinc-200 p-10 md:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <h3 className="font-display text-3xl md:text-4xl text-zinc-900 tracking-tighter">
              Want to work with us?
            </h3>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-3.5 text-sm font-medium hover:bg-zinc-700 transition-colors self-start md:self-auto"
            >
              Start a conversation <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
